// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 누르는 것들
//
//  터치 규격은 넷이고, **자리마다 즉흥으로 고르지 마라**:
//      44px  최소 (WCAG 2.5.5 · Apple HIG). 가로도 44px 이어야 한다 — 장갑 낀 엄지의
//            접촉 타원 짧은 축이 360px 폰 기준 34~42 CSS px 이라, 34×44 짜리 버튼은
//            **접촉면이 버튼보다 넓어서** 옆 것을 같이 누른다.
//      48px  목록의 한 행
//      56px  화면의 주동작 (`PrimaryButton`)
//       8px  인접한 터치 대상 사이 최소 간격 (`.cv-touchrow`)
//
//  ⚠️ 파괴적 동작은 **간격이 아니라 다른 열**로 떼어 놓는다. 그리고 폰에는 열이
//     하나뿐이므로 폰에서 「다른 열」은 **「다른 화면」**으로 번역한다 —
//     목록 행에 삭제·회수를 놓지 말고 한 탭 더 들어간 상세에만 둬라.
// ─────────────────────────────────────────────────────────────────────────

import { Button, type ButtonProps } from '@mantine/core';
import type { ReactNode } from 'react';

type Common = ButtonProps & {
  onClick?: () => void;
  type?: 'button' | 'submit';
  children: ReactNode;
};

/**
 * 화면의 주동작. **화면당 하나.**
 *
 * ★ 56px 인 이유는 예뻐서가 아니다. 장갑 낀 손으로 흔들리는 발판 위에서 누른다.
 * ★ 폰에서는 이 버튼을 **화면 아래 1/3** 에 둬라 — `<Shell bottomBar={…}>` 또는
 *   `<div className="cv-bottombar">`. 위쪽에는 엄지가 안 닿는다.
 */
export function PrimaryButton({ children, ...rest }: Common) {
  return (
    <Button
      size="lg"
      fullWidth
      /**
       * ★ `height` 가 아니라 `minHeight` 다. 그리고 라벨 줄바꿈을 허용한다.
       *
       * ⚠️ Mantine Button 은 라벨에 `white-space: nowrap` 을, 뿌리에 고정 `height` 를 준다.
       *    그래서 한글 라벨이 조금만 길어지면 **말없이 잘린다**(말줄임표도 없이 폭에서 넘친다).
       *    실측: 320px 폰에서 「코드 다시 보내기 (59초)」 라벨이 scrollWidth 219 · clientWidth 190
       *    으로 **잘렸다.** 하필 잘리는 부분이 **남은 초**라, 카운트다운의 유일한 쓸모가
       *    사라진다. [실측 2026-08-06 · 320px]
       *    한글 문구는 번역도 없이 자주 바뀌므로, 자리마다 글자 수를 세지 말고
       *    **구조적으로** 안 잘리게 만든다.
       */
      {...rest}
      styles={{ label: { whiteSpace: 'normal' } }}
      style={{ height: 'auto', minHeight: 56, ...rest.style }}
    >
      {children}
    </Button>
  );
}

/**
 * 되돌릴 수 없는 동작.
 *
 * ⚠️ 이 버튼을 주동작 **옆에** 두지 마라. 다른 열·다른 블록·다른 화면이다.
 * ⚠️ 그리고 가능하면 확인창보다 **되돌리기 토스트**를 써라. 장갑에서는 확인창이
 *    누를 대상을 하나 더 늘릴 뿐이고, 사람은 확인창을 읽지 않고 통과시킨다.
 *    확인창이 값을 하는 자리는 **드물고 진짜로 되돌릴 수 없는 것** 하나뿐이다
 *    (예: 현장 주소 확정 — 지워도 그 주소는 영구 점유된다).
 */
export function DangerButton({ children, ...rest }: Common) {
  return (
    <Button color="danger" variant="outline" size="md" {...rest}>
      {children}
    </Button>
  );
}
